/** The acting roles, as `@brush/auth` spells them; restated so this module imports nothing and runs under `node --test`. */
export type Role = 'admin' | 'room' | 'player';

/** A player staff chose to look at, and the role they were acting as when they chose. */
export interface Browse {
  id: string;
  role: Role | null;
}

export interface PortalSubject {
  /** The player whose portal is on screen; null where staff have not chosen one yet (the picker). */
  playerId: string | null;
  /** The portal on screen is somebody else's. */
  readOnly: boolean;
}

/**
 * Whose portal is on screen — `player.tsx`'s whole rule, in one place.
 *
 * <b>Staff browse; players read their own card; the acting role decides which.</b> A browse belongs to the role it was
 * made under and counts only while that role is still in force and may browse (#526). Without that, a browse made as
 * an administrator outlived a switch to Player — still read-only, still somebody else's — or, where the new role could
 * not read that player, turned into a 404 and "Couldn't reach the Brush API" until a reload. Ending it by comparing
 * roles, rather than by clearing state when the role changes, means no render can ever show the stale pairing.
 */
export function portalSubject(input: {
  canBrowse: boolean;
  actingRole: Role | null;
  browse: Browse | null;
  membershipPlayerId: string | null;
}): PortalSubject {
  const browsing = input.canBrowse && input.browse !== null && input.browse.role === input.actingRole;
  if (browsing) return { playerId: input.browse!.id, readOnly: true };
  return { playerId: input.canBrowse ? null : input.membershipPlayerId, readOnly: false };
}
